import React, {useCallback, useEffect, useState} from 'react'
import {APIState} from '_constants/APIState';
import {Spinner} from 'components/common/Spinner';
import {
  CumulativeData,
  DateEx,
  FilterUserAverageScoreRange,
  IPredictionAdmin,
  IQuoteFull,
  IStockSymbolAdmin,
  LinePoints,
  MACD,
  PointUpDown,
  PredictionTypeEnum,
  SignalsData,
  SignalsMap,
  StatsData,
  StockHelper,
  StockSignalsEnum
} from 'predictagram-lib';
import {adminApiServiceCommon, adminPredictionApiService} from 'services/AdminApiService';
import {Form, Formik} from "formik";
import {PredictionTypesDropdown} from "../../common/PredictionsDropDown";
import {predictionService} from "../../../services";
import {ChartModel as ChartOptions} from "../../../models/chart.model";
import {IChart} from "../../../interfaces/chart/IChart";
import {PredictionChart} from "../../public/ListingsPage/components/Prediction/PredictionChart";
import * as Plotly from "plotly.js";
import {CumulativeChartModel} from "../../../models/cumulative-chart.model";
import {DropDown} from "../../common/form/DropDown";
import {Checkbox} from "../../common/form/Checkbox";
import {AnalysisFilters} from "../../common/analysis/analysis.filters";
import {DatePickerTzEst} from "../../common/DatePickerTz";
import {MultiSymbolsDropDown} from "../../common/analysis/MultiSymbolsDropDown";
import {FieldWithError} from "../../common";
import {SymbolIdsDropdown} from "../../common/SymbolIdsDropdown";
import {StrategyProfitModel} from "../../../models/strategy-profit.model";

interface SearchOptions {
  predictionTypes: PredictionTypeEnum[] | undefined,
  startTimePredMade: number,
  endTimePredMade: number,
  limit?: number | undefined,
  stockSymbolIds: number[],
  extraSymbolIds: number[],
  // showSupResLinesChecked?:boolean,
  userAverageScore?: FilterUserAverageScoreRange,
}
const defaultSymbol = 3; // SPY

const toIntOrDefault = (v:any, def=null)=>{
  return v!=='' ? parseInt(v, 10): def;
}

export const SearchFilter: React.FunctionComponent<{
  initialValues: any,
  onClick: (search: any) => void,
}> = ({ initialValues, onClick }) => {



  const onSubmit = (data: any, actions: any) => {
    const s = {
      ...data,
      predictionTypes: data.predictionTypes?.length ? data.predictionTypes.map((v: any) => parseInt(v)) : undefined,
      stockSymbolIds: data.stockSymbolIds ? [data.stockSymbolIds] : [defaultSymbol],
      // showSupResLinesChecked: !!data.showSupResLinesChecked,
    };
    if (s.userIds?.length) {
      s.userIds = (s.userIds as any as string).split(',').map(v => +v);
    } else {
      s.userIds = undefined;
    }
    const score = +s.userAverageScore;
    if (score > 0 ) {
      s.userAverageScore = {
        days: 30,
        minScoreRaw: score,
      };
    } else if (score < 0) {
      s.userAverageScore = {
        days: 30,
        maxScoreRaw: score*-1,
      };
    } else {
      delete s.userAverageScore;
    }

    onClick(s);
  }

  return (
    <div>
      <Formik initialValues={initialValues} onSubmit={onSubmit}>
        {({ values, touched, errors, setFieldValue, isSubmitting }) => {
          return <Form>
            <div className="d-flex justify-content-between align-items-start gap-2 my-3">

              <div className="flex-grow-0">
                <PredictionTypesDropdown name="predictionTypes" multiple={true} showDefault={false} errors={errors} touched={touched} />
                <FieldWithError errors={errors} touched={touched} fieldName="userIds" label="User IDs" />
                <MultiSymbolsDropDown errors={errors} touched={touched} name="extraSymbolIds" />
              </div>
              <div className="d-flex flex-column gap-2 " style={{width: '100px'}} >

                <DropDown enumMap={AnalysisFilters.UsersScore}
                  errors={errors}
                  touched={touched}
                  label="Last30d Score" name="userAverageScore" />
                <SymbolIdsDropdown name="stockSymbolIds" errors={errors} touched={touched} showDefault={false} defaultKey={defaultSymbol} />
              </div>

              <div className="d-flex flex-column gap-2">
                <div className="d-flex flex-column">
                  <span>Start Date EST</span>
                  <DatePickerTzEst showTimeSelect dateFormat="MM/dd/yyyy hh:mm aa"
                    onChange={(date: Date) => {
                      setFieldValue("startTimePredMade", date.getTime() / 1000);
                      // console.debug({dt:DateHelper.dateTimeFormatUs(date), ts: date.getTime() / 1000});
                    }} selected={new Date(values.startTimePredMade * 1000)}
                  />
                </div>

                <div className="d-flex flex-column">
                  <span>End Date EST</span>
                  <DatePickerTzEst showTimeSelect dateFormat="MM/dd/yyyy hh:mm aa" onChange={(date: Date) => {
                    setFieldValue("endTimePredMade", date.getTime() / 1000);
                  }}
                    selected={new Date(values.endTimePredMade * 1000)}
                  />
                </div>
              </div>

              <div className="align-self-center">
                <button type="submit" className="btn btn-primary p-2" /*disabled={isSubmitting}*/>Update Search</button>
              </div>


            </div>

          </Form>
        }}
      </Formik>
    </div>
  )
}

interface StatsDataEx extends StatsData {
  predStats: readonly (IPredictionAdmin&{fromStockSymbolId:number})[],
}
export const AdminPredictionsCumulativeData: React.FunctionComponent = () => {

  const prevDay = StockHelper.findTradingDay(new Date(), -1, true);
  const wh = StockHelper.workingHours(new Date(prevDay.now()*1000));
  const initialValues: SearchOptions = {
    predictionTypes: StrategyProfitModel.defVals.predictionTypes,
    startTimePredMade: wh.start.getTimeSec(),
    endTimePredMade: wh.end.getTimeSec(),
    stockSymbolIds: defaultSymbol as any,
    extraSymbolIds: [],
    // showSupResLinesChecked: false,
  };

  const [apiState, setApiState] = useState<APIState>(APIState.IDLE);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [options, setOptions] = useState<SearchOptions>(initialValues);
  const [chartData, setChartData] = useState<IChart | undefined>(undefined);
  const [macdChart, setMacdChart] = useState<IChart | undefined>(undefined);
  const [data, setData] = useState<StatsDataEx>({ stockStats: [], predStats: []});
  // const [supResData, setSupResData] = useState<SupResLineData|null>(null);
  const [showLegend, setShowLegend] = useState<boolean>(true);
  const [prevDayClosePrice, setPrevDayClosePrice] = useState<IQuoteFull>(false as any);

  const [currSymbol, setCurrSymbol] = useState<IStockSymbolAdmin>();

  const onClickCallback = (searchOptions: SearchOptions) => {
    const data = {
      ...searchOptions,
      limit: 10_000,
    } as any;
    setOptions(data);
  }

  const load = useCallback(async () => {
    try {
      setApiState(APIState.LOADING);
      const start = new DateEx(options.startTimePredMade * 1000);
      const end = new DateEx(options.endTimePredMade * 1000);
      const symbolIds = options.stockSymbolIds;

      const symbols = await adminApiServiceCommon.getSymbols();
      const stockSymbolObj = symbols.find((v )=>v.id==options.stockSymbolIds[0]) as IStockSymbolAdmin;
      setCurrSymbol(stockSymbolObj);
      const stockSymbol = stockSymbolObj.name;
      //const stockSymbol =  (options.symbolNames as any)[0];
      if (options.extraSymbolIds?.length) {
        options.extraSymbolIds.forEach(v=>symbolIds.push(parseInt(v as any)));
      }
      const stats = await adminPredictionApiService.getBarsData(stockSymbol, start, end);
      const items = await adminPredictionApiService.cumulativeData(options);

      const prev = StockHelper.workingHours(new Date(StockHelper.findTradingDay(start, -1, false).openAt()*1000));
      const prevDate = prev.end;
      const lastClose = await adminPredictionApiService.getBarsData(stockSymbol, prevDate, prevDate);
      if (lastClose && lastClose.length==1) {
        setPrevDayClosePrice(lastClose[0]);
      }
      setData({ predStats: items.map(a => a) as any, stockStats: stats});
      setApiState(APIState.LOADED);
    } catch (error) {
      setApiState(APIState.LOADED);
      setError(error as Error);
    }
  }, [options]);

  const renderChart = async (stats: StatsData) => {



    const d = ChartOptions.prebuildChartDay({
      stats: stats.stockStats,
      endFillTimeSec: options.endTimePredMade,
      chartWidth: 400
    });
    const stockLine:LinePoints = {x:[], y:[]};
    stats.stockStats.forEach((v:any)=>{
      stockLine.x.push(v.t);
      stockLine.y.push(v.c);
    });

    const cumDataAll = CumulativeData.calcCumulativeChartData(stats, stockLine, {
      prevClose: prevDayClosePrice.c,
      prevCloseThreshold: 0,
    });
    const cumData = cumDataAll.cumulStats;
    const minMaxData = cumDataAll.minMaxStats;

    const signals = cumDataAll.signals;
    const yMin = cumDataAll.yMin;
    const yMax = cumDataAll.yMax;
    type cLine = LinePoints & {color: string|string[], isRender: boolean, mode: any , lineMode?:any, markerSize?:number};

    type SignalsUpDown = {signals: PointUpDown[], buyColor: string, sellColor: string, isRender:boolean};

    const predVals:LinePoints = {x:[], y:[]};
    const predValsOthers:LinePoints = {x:[], y:[]};
    for (const p of stats.predStats) {
      const isOther = !!(p as any).fromStockSymbolId;
      const arr = isOther ? predValsOthers : predVals;
      arr.x.push(p.valueTime);
      arr.y.push(p.value);
    }
    const signalColors = [
      // ['#10451D','#c30010',],
      // ['#155D27', '#d1001f'],
      // ['#1A7431','#de0a26',],
      // [ '#208B3A', '#f01e2c'],
      // ['#25A244', '#ff2c2c'],
      // ['#2DC653', '#f94449'],
      // ['#4AD66D', '#ee6b6e'],
      // ['#6EDE8A', '#f69697'],
      ['#92E6A7', '#ffcbd1'],
    ];


    const cumulStats  = {
      lines : {
        predVals: { x: predVals.x, y: predVals.y, color: '#ED7014', isRender: predValsChecked, mode: 'markers' },
        predValsOthers: { x: predValsOthers.x, y: predValsOthers.y, color: '#edc214', isRender: predValsChecked, mode: 'markers' },
        last5predsAvg: { x: cumData.last5predsAvg.x, y: cumData.last5predsAvg.y, color: '#c0d6e4', isRender: last5AvgChecked, mode: 'lines+markers' },
        last3predsAvg: { x: cumData.last3predsAvg.x, y: cumData.last3predsAvg.y, color: '#c39797', isRender: last3AvgChecked, mode: 'lines+markers' },
        last15minAvg: { x: cumData.last15minAvg.x, y: cumData.last15minAvg.y, color: '#22AA99', isRender: last15minAvgChecked, mode: 'lines+markers' },
        last30minAvg: { x: cumData.last30minAvg.x, y: cumData.last30minAvg.y, color: '#AAAA11', isRender: last30minAvgChecked, mode: 'lines+markers' },
        last1hAvg:    { x: cumData.last1hAvg.x, y: cumData.last1hAvg.y, color: '#59643f', isRender: last1hAvgChecked, mode: 'lines+markers' },
        last2hAvg:    { x: cumData.last2hAvg.x, y: cumData.last2hAvg.y, color: '#88598f', isRender: last2hAvgChecked, mode: 'lines+markers' },
        last3hAvg:    { x: cumData.last3hAvg.x, y: cumData.last3hAvg.y, color: '#aa6d11', isRender: last3hAvgChecked, mode: 'lines+markers' },
        last4hAvg:    { x: cumData.last4hAvg.x, y: cumData.last4hAvg.y, color: '#757e98', isRender: last4hAvgChecked, mode: 'lines+markers' },

        // min/max 4h avg
        min4hAvg:    { x: minMaxData.stats4hAvg.min.x, y: minMaxData.stats4hAvg.min.y, color: '#ce2093', isRender: cumulativeMinMax4hChecked, mode: 'lines', lineMode: 'dash'},
        max4hAvg:    { x: minMaxData.stats4hAvg.max.x, y: minMaxData.stats4hAvg.max.y, color: '#56931b', isRender: cumulativeMinMax4hChecked, mode: 'lines', lineMode: 'dash'},
        min4h60Avg:    { x: minMaxData.stats4h160pctAvg.min.x, y: minMaxData.stats4h160pctAvg.min.y, color: '#ce2093', isRender: cumulativeMinMax4hChecked, mode: 'lines' },
        max4h60Avg:    { x: minMaxData.stats4h160pctAvg.max.x, y: minMaxData.stats4h160pctAvg.max.y, color: '#56931b', isRender: cumulativeMinMax4hChecked, mode: 'lines' },

        // min/max 4h avg
        min2hAvg:    { x: minMaxData.stats2hAvg.min.x, y: minMaxData.stats2hAvg.min.y, color: '#92286e', isRender: cumulativeMinMax2hChecked, mode: 'lines', lineMode: 'dash'},
        max2hAvg:    { x: minMaxData.stats2hAvg.max.x, y: minMaxData.stats2hAvg.max.y, color: '#30441d', isRender: cumulativeMinMax2hChecked, mode: 'lines', lineMode: 'dash'},
        min2h160pctAvg:    { x: minMaxData.stats2h160pctAvg.min.x, y: minMaxData.stats2h160pctAvg.min.y, color: '#822f66', isRender: cumulativeMinMax2hChecked, mode: 'lines' },
        max2h160pctAvg:    { x: minMaxData.stats2h160pctAvg.max.x, y: minMaxData.stats2h160pctAvg.max.y, color: '#3e6917', isRender: cumulativeMinMax2hChecked, mode: 'lines' },
        min2h220pctAvg:    { x: minMaxData.stats2h220pctAvg.min.x, y: minMaxData.stats2h220pctAvg.min.y, color: '#d41818', isRender: cumulativeMinMax2hChecked, mode: 'lines' },
        max2h220pctAvg:    { x: minMaxData.stats2h220pctAvg.max.x, y: minMaxData.stats2h220pctAvg.max.y, color: '#27ce12', isRender: cumulativeMinMax2hChecked, mode: 'lines' },


        min2hAvgOld:    { x: cumDataAll.old.min2hAvg.x, y: cumDataAll.old.min2hAvg.y, color: '#92286e', isRender: cumulativeMinMax2hOldChecked, mode: 'lines', lineMode: 'dash'},
        max2hAvgOld:    { x: cumDataAll.old.max2hAvg.x, y: cumDataAll.old.max2hAvg.y, color: '#30441d', isRender: cumulativeMinMax2hOldChecked, mode: 'lines', lineMode: 'dash'},
        min2h160pctOldAvg:    { x: cumDataAll.old.min2h60Avg.x, y: cumDataAll.old.min2h60Avg.y, color: '#822f66', isRender: cumulativeMinMax2hOldChecked, mode: 'lines' },
        max2h160pctOldAvg:    { x: cumDataAll.old.max2h60Avg.x, y: cumDataAll.old.max2h60Avg.y, color: '#3e6917', isRender: cumulativeMinMax2hOldChecked, mode: 'lines' },
        min2h220pctOldAvg:    { x: cumDataAll.old.min2h220pctAvg.x, y: cumDataAll.old.min2h220pctAvg.y, color: '#d41818', isRender: cumulativeMinMax2hOldChecked, mode: 'lines' },
        max2h220pctOldAvg:    { x: cumDataAll.old.max2h220pctAvg.x, y: cumDataAll.old.max2h220pctAvg.y, color: '#27ce12', isRender: cumulativeMinMax2hOldChecked, mode: 'lines' },

        ema6:  { x: cumData.ema6.x, y: cumData.ema6.y, color: '#ccec76', isRender: macdChecked, mode: 'lines' },
        ema12:  { x: cumData.ema12.x, y: cumData.ema12.y, color: '#536917', isRender: macdChecked, mode: 'lines' },
        ema26:  { x: cumData.ema26.x, y: cumData.ema26.y, color: '#bb1f2f', isRender: macdChecked, mode: 'lines' },
        ema120:  { x: cumData.ema120.x, y: cumData.ema120.y, color: '#6f2c33', isRender: macdChecked, mode: 'lines' },
        vwap:   { x: cumData.vwap.x, y: cumData.vwap.y, color: '#bb1f2f', isRender: vwapChecked, mode: 'lines' },

        prevCloseLine:   { x: cumData.prevCloseLine.x, y: cumData.prevCloseLine.y, color: '#F9DDDDFF', isRender: lastPricesSupRes, mode: 'lines', lineMode: 'dash' },
        openLine:   { x: cumData.prevCloseLine.x, y: cumData.openLine.y, color: '#F9DDDDFF', isRender: lastPricesSupRes, mode: 'lines', lineMode: 'dash' },

      } as {[key:string]:cLine},
      signals: {
        signals4h: {isRender: signals4hChecked, signals: signals.signals4h, buyColor: '#10451D', sellColor: '#c30010'},
        // signals4h: { x: cumData.signals4h.x, y: cumData.signals4h.y, markerSize:0.01, color: signalColors[0], isRender: signals4hChecked, mode: 'markers' } as cLine,

        signals2h: {isRender: signals2hChecked, signals: signals.signals2h, buyColor: '#18ac7f', sellColor: '#ae4acd'},
        signals2hMinMax220pctPred: {isRender: signals2hMinMax220pctPredChecked, signals: signals.signals2hMinMax220pctPred, buyColor: '#75ffd3', sellColor: '#642a76'},
        signals2hMinMax220pctStock: {isRender: signals2hMinMax220pctStockChecked, signals: signals.signals2hMinMax220pctStock, buyColor: '#cdff03', sellColor: '#642a06'},
        signals2hMinMax160pctStock: {isRender: signals2hMinMax160pctStockChecked, signals: signals.signals2hMinMax160pctStock, buyColor: '#03ff64', sellColor: '#645906'},

        signals2hMinMax220pctStockInside: {isRender: signals2hMinMax220pctStockInsideChecked, signals: signals.signals2hMinMax220pctStockInside, buyColor: '#ffd503', sellColor: '#642a06'},
        signals2hMinMax160pctStockInside: {isRender: signals2hMinMax160pctStockInsideChecked, signals: signals.signals2hMinMax160pctStockInside, buyColor: '#03ffb3', sellColor: '#645906'},


        signals2hMinMax220pctStock3Pred: {isRender: signals2hMinMax220pctStock3PredChecked, signals: signals.signals2hMinMax220pctStock3Pred, buyColor: '#75ff03', sellColor: '#640606'},

        signalsLast3pred: {isRender: signalsLast3predChecked, signals: signals.signalsLast3pred, buyColor: '#b8e9c5', sellColor: '#edabb3'},
        // signalsLast3pred: { x: cumData.signalsLast3p.x, y: cumData.signalsLast3p.y, markerSize:0.01, color: signalColors[2], isRender: signalsLast3predChecked, mode: 'markers' } as cLine,

        signalsLast15m: {isRender: signalsLast15minChecked,  signals: signals.signalsLast15m, buyColor: '#5ecaba', sellColor: '#8c1a6a'},
        // signalsLast15m: { x: cumData.signalsLast15m.x, y: cumData.signalsLast15m.y, markerSize:0.01, color: signalColors[3], isRender: signalsLast15minChecked, mode: 'markers' } as cLine,

        signalsEma6Price: {isRender: signalsEma6PriceChecked, signals: signals.signalsEma6Price, buyColor: '#2e6a79', sellColor: '#ca6868'},
        signalsEma12Price: {isRender: signalsEma12PriceChecked, signals: signals.signalsEma12Price, buyColor: '#3d4e52', sellColor: '#8d6262'},
        signalsEma26Price: {isRender: signalsEma26PriceChecked, signals: signals.signalsEma26Price, buyColor: '#3ea7c1', sellColor: '#f9dddd'},
        signalsEma120Price: {isRender: signalsEma120PriceChecked, signals: signals.signalsEma120Price, buyColor: '#6ea0ac', sellColor: '#9a6767'},

        signalsMacdGross: { isRender: signalsMacdCrossChecked, signals: signals.signalsMacdCross, buyColor: '#73a197', sellColor:'#f94449'},
        // signalsMacdCross: { x: cumData.signalsMacdCross.x, y: cumData.signalsMacdCross.y, markerSize:0.01, color: signalColors[5], isRender: signalsMacdCrossChecked, mode: 'markers' } as cLine,
        signalsVwapPrice: {isRender: signalsVwapPriceChecked, signals: signals.signalsVwapPrice, buyColor: '#2738aa', sellColor: '#ff2500'},
        // signalsVwapPrice: { x: cumData.signalsVwapPrice.x, y: cumData.signalsVwapPrice.y, markerSize:0.01, color: signalColors[6], isRender: signalsVwapPriceChecked, mode: 'markers' } as cLine,
        signalsConsensus3Pred10m: {isRender: signalsCons3Pred10MinChecked, signals: signals.signalsConsensus3Pred10m, buyColor: '#6EDE8A', sellColor: '#f69697',},
        signalsConsensus4Pred10m: {isRender: signalsCons4Pred10MinChecked, signals: signals.signalsConsensus4Pred10m, buyColor: '#b9de6e', sellColor: '#f696ee',},
        signalsConsensus5Pred10m: {isRender: signalsCons5Pred10MinChecked, signals: signals.signalsConsensus5Pred10m, buyColor: '#ded16e', sellColor: '#d996f6',},

        signalsSupRes1hStock: {isRender: signalsSupRes1hStockChecked, signals: signals[StockSignalsEnum.SIGNALS_SUP_RES_1H_STOCK], buyColor: '#9a946b', sellColor: '#611a80',},

        signalsPrevClose: {isRender: lastPricesSupRes, signals: signals[StockSignalsEnum.SIGNALS_PREV_ClOSE_LINE_STOCK], buyColor: '#9a946b', sellColor: '#611a80',},
        signalsDayOpen: {isRender: lastPricesSupRes, signals: signals[StockSignalsEnum.SIGNALS_OPEN_LINE_STOCK], buyColor: '#9a946b', sellColor: '#611a80',},

        signalsLast2hAvgStock: {isRender: signalsLast2hAvgStockChecked, signals: signals[StockSignalsEnum.SIGNALS_LAST_2H_AVG_STOCK], buyColor: '#9a946b', sellColor: '#611a80',},
      } as {[key:string]:SignalsUpDown},
    };

    // const dots = {x:xDots, y:yDots, mode:'markers', name:'dots',marker:{size:4}, line:{width:1}, /*line: {dash:'solid', width:1, color:color,}*/ hoverinfo:'none'} as Plotly.PlotData;
    const lines: Plotly.PlotData[] = [];
    for (const k in cumulStats.lines) {
      const v = (cumulStats.lines as any)[k] as cLine;
      if (v.isRender) {
        const l = { x: v.x, y: v.y, mode: v.mode, name: k, line: { width: 1 , dash: v.lineMode}, marker: { size: v?.markerSize||4, color: v.color }, hoverinfo: 'none' } as Plotly.PlotData;
        // l.hoverinfo = 'y';
        lines.push(l);
      }

    }

    let chartYMin: number = null as any;
    let chartYMax: number = null as any;

    if (cumulativeMinMaxChecked) {
      const cumData = await new CumulativeChartModel(predictionService).preBuildStats(
        currSymbol?.name as string, new DateEx(options.startTimePredMade * 1000), new DateEx(options.endTimePredMade * 1000), options.predictionTypes || []
      );
      lines.push(...[cumData.maxLine, cumData.minLine, cumData.min60Line, cumData.max60Line]);
      chartYMin = yMin && cumData.chartYMin ? Math.min(yMin, d.dataLowDay, cumData.chartYMin) : d.dataLowDay;
      chartYMax = yMax && cumData.chartYMax ? Math.max(yMax, d.dataHighDay, cumData.chartYMax) : d.dataHighDay;

    } else {
      chartYMin = yMin ? Math.min(yMin, d.dataLowDay) : d.dataLowDay;
      chartYMax = yMax ? Math.max(yMax, d.dataHighDay) : d.dataHighDay;
    }

    const cData = ChartOptions.plotData('main', d.plotLines.during, ChartOptions.plotLineColor);
    // cData.hovertemplate = 'y: %{y}'; // %{x|%H:%M}
    cData.hoverinfo = "y";
    const chart: IChart = ChartOptions.dailyChartTemplate({
      tickVals: d.tickVals, tickText: d.tickText, formatStr: ChartOptions.formatPriceStr(d.dataHighDay),
      yMin: chartYMin, yMax: chartYMax,
      plotData: [
        cData,
        ...lines
      ],
      rangeBreaks: d.chartDataPlotlyXGaps, rangeBreaksLines: d.chartDatePlotlyXGapsLines
    }) as IChart;


    const renderSignals = (points:PointUpDown[], buyColor: string='#5fbf3b', sellColor: string='#df3434')=>{
      points.forEach(s=>{
        chart.layout.annotations.push({
          text: '',
          showarrow: true,
          x: s.x,
          y: s.y,
          arrowcolor: s.isSellSignal ? sellColor : buyColor, // ''; cumulStats.signals.color,
          arrowside: 'end',
          ax: s.x,
          axref: "x",
          ay: s.y + (s.isSellSignal ? 1 : -1)*(chartYMax-chartYMin)/6,
          ayref: "y",
          startstandoff: 10,
        });
      });
    }

    for (const sList of Object.values(cumulStats.signals)) {
      if (sList.isRender) {
        const signals = cancellationInterval ?
                        SignalsData.signalsCalcAdjust(sList.signals, {
                          cancellationSecs: cancellationInterval,
                          endTimeSecs: options.endTimePredMade,
                        }):
                        sList.signals;


        renderSignals(signals, sList.buyColor, sList.sellColor);
      }
    }

    const colors = [
      '#7873B066',
      'rgba(112,112,118,0.4)',
      'rgba(172,166,241,0.4)',
      'rgba(83,70,97,0.4)',
      'rgba(79,77,107,0.4)',
      'rgba(155,151,197,0.4)',
      'rgba(196,193,219,0.4)',
      'rgba(219,219,240,0.4)',
    ];
    let count = 0;
    // render horizontal lines
    if (signalsSupRes1hStockChecked || supResLinesChecked) {
      // console.debug({supResData});
      // Pivot is calculated with the formula P = (H+L+C)/3, where P=Pivot, H=High, L=Low and C=Close
      // To calculate Support / Resistance:
      // R1 = 2P - L, S1 = 2P - H
      // R2 = (P - S1) +R1, S2 = P - (R2 - S1)
      // const prevDayLines: SupResLine = SupResLinesData.calcSupResLines(supResData);
      // const currDayLines = SupResLinesData.hourlyLines(stats.stockStats as any);
      // // currDayLines.unshift(prevDayLines);
      // console.debug(currDayLines);
      // const start = options.startTimePredMade;
      const end = options.endTimePredMade;
      for (const l of cumDataAll.cumulStats.supResLines) {

        [l.r1,l.s1,l.r2,l.s2].forEach(v=>{
          chart.layout.shapes.push({ // curr value horizontal line
            type: 'line',  x0: l.tLast, y0: v, x1: end, y1: v, layer: "below",
            line: {color: colors[count%(colors.length-1)], width: 2, dash: 'dash'}
          } as Partial<Plotly.Shape>);
        });
        count++;
      }
    }
    const firstP = stats.stockStats.length>0 ? stats.stockStats[0]: null;
    if (firstP) {
      const lastIndex = stats.stockStats.length - 1;
      const lastP = stats.stockStats[lastIndex];

      if (trendLinesChecked) {
        const data = SignalsData.calcTrendLineRegression(stats.stockStats);
        [data.high, data.low].forEach(v => {
          const y0 = v.y(0);
          const y1 = v.y(lastIndex);
          chart.layout.shapes.push({ // curr value horizontal line
            type: 'line',
            x0: firstP.t, y0: y0,
            x1: lastP.t, y1: y1, layer: "below",
            line: {color: '#ebc8f4', width: 1, dash: 'dash'}
          } as Partial<Plotly.Shape>);
        });
        count++;
      }
    }
    // @ts-ignore
    chart.config.scrollZoom = true;
    // @ts-ignore
    chart.config.displayModeBar = true;
    // chart.layout.height = 500;
    // chart.divId = `${symbol}-${timeSecsLatest}`;
    //setItems(items);
    chart.layout.paper_bgcolor = 'rgb(0,0,0)';
    chart.layout.showlegend = true;
    chart.layout.legend = { x: 0, xanchor: 'left', y: 0 };
    chart.layout.showlegend = showLegend;
    chart.layout.hovermode = 'x unified';
    // chart.layout.hoverlabel = {
    //   // bgcolor: "#fff",
    //   // bordercolor: '#fff',
    //   // font: 16,
    //   // font_family: "Rockwell"
    // };
    const macdChartParams = {
      tickVals: d.tickVals, tickText: d.tickText, formatStr: ChartOptions.formatPriceStr(d.dataHighDay),
      yMin: cumData.macd.yMin, yMax: cumData.macd.yMax,
      plotData: [
        { x: cumData.macd.x, y: cumData.macd.y, mode: 'lines', name: 'MACD', line: { width: 1}, hoverinfo: 'none' } as any as Plotly.PlotData
      ],
    } as any;
    // console.debug({macdChartParams});
    const mChart: IChart = ChartOptions.dailyChartTemplate(macdChartParams);
    // macdChart.layout = Object.assign({}, chart.layout);
    mChart.divId = "mchart";
    mChart.layout.height = 200;
    mChart.layout.paper_bgcolor = 'rgb(0,0,0)';
    mChart.layout.showlegend = true;
    mChart.layout.legend = { x: 0, xanchor: 'left', y: 1 };

    setChartData(chart);
    setMacdChart(mChart);
  };

  useEffect(() => {
    load();
  }, [load])

  const [predValsChecked, setPredValsChecked] = React.useState(true);
  const [signals4hChecked, setSignals4hChecked] = React.useState(false);
  const [signals2hChecked, setSignals2hChecked] = React.useState(false);
  const [signalsSupRes1hStockChecked, setSignalsSupRes1hStockChecked] = React.useState(false);


  const [signalsLast2hAvgStockChecked, setSignalsLast2hAvgStockChecked] = React.useState(false);
  const [signals2hMinMax220pctPredChecked, setSignals2hMinMax220pctPredChecked] = React.useState(false);
  const [signals2hMinMax220pctStockChecked, setSignals2hMinMax220pctStockChecked] = React.useState(false);
  const [signals2hMinMax160pctStockChecked, setSignals2hMinMax160pctStockChecked] = React.useState(false);

  const [signals2hMinMax220pctStockInsideChecked, setSignals2hMinMax220pctStockInsideChecked] = React.useState(false);
  const [signals2hMinMax160pctStockInsideChecked, setSignals2hMinMax160pctStockInsideChecked] = React.useState(false);

  const [signals2hMinMax220pctStock3PredChecked,setSignals2hMinMax220pctStock3PredChecked] = React.useState(false);
  const [signalsLast3predChecked, setSignalsLast3predChecked] = React.useState(false);
  const [signalsLast15minChecked, setSignalsLast15minChecked] = React.useState(false);

  const [signalsEma6PriceChecked, setSignalsEma6PriceChecked] = React.useState(false);
  const [signalsEma12PriceChecked, setSignalsEma12PriceChecked] = React.useState(false);
  const [signalsEma26PriceChecked, setSignalsEma26PriceChecked] = React.useState(false);
  const [signalsEma120PriceChecked, setSignalsEma120PriceChecked] = React.useState(false);
  const [signalsMacdCrossChecked, setSignalsMacdCrossChecked] = React.useState(false);
  const [signalsVwapPriceChecked, setGignalsVwapPriceChecked] = React.useState(false);
  const [signalsCons3Pred10MinChecked, setSignalsCons3Pred10MinChecked] = React.useState(false);
  const [signalsCons4Pred10MinChecked, setSignalsCons4Pred10MinChecked] = React.useState(false);
  const [signalsCons5Pred10MinChecked, setSignalsCons5Pred10MinChecked] = React.useState(false);
  const [cancellationInterval,setCancellationInterval] = React.useState<number|null>(null);

  const [last5AvgChecked, setLast5AvgChecked] = React.useState(false);
  const [last3AvgChecked, setLast3AvgChecked] = React.useState(false);
  const [last15minAvgChecked, setLast15minAvgChecked] = React.useState(false);
  const [last30minAvgChecked, setLast30minAvgChecked] = React.useState(false);
  const [supResLinesChecked, setSupResLinesChecked] = React.useState(false);
  const [trendLinesChecked, setTrendLinesChecked] = React.useState(false);
  const [lastPricesSupRes, setLastPricesSupRes] = React.useState(false);
  const [last1hAvgChecked, setLast1hAvgChecked] = React.useState(false);
  const [last2hAvgChecked, setLast2hAvgChecked] = React.useState(false);
  const [last3hAvgChecked, setLast3hAvgChecked] = React.useState(false);
  const [last4hAvgChecked, setLast4hAvgChecked] = React.useState(false);
  const [cumulativeMinMaxChecked, setCumulativeMinMaxChecked] = React.useState(false);
  const [cumulativeMinMax4hChecked, setCumulativeMinMax4hChecked] = React.useState(false);
  const [cumulativeMinMax2hChecked, setCumulativeMinMax2hChecked] = React.useState(false);
  const [cumulativeMinMax2hOldChecked, setCumulativeMinMax2hOldChecked] = React.useState(false);
  const [macdChecked, setMacdChecked] = React.useState(false);
  const [vwapChecked, setVwapChecked] = React.useState(false);

  useEffect(() => {
    // setApiState(APIState.LOADING);
    renderChart(data);
    // setApiState(APIState.LOADED);
  }, [
    data, /*supResData,*/ last3AvgChecked, last5AvgChecked, last1hAvgChecked, last2hAvgChecked, last3hAvgChecked, last4hAvgChecked,
    predValsChecked, signals2hChecked, signals4hChecked, last15minAvgChecked, last30minAvgChecked,
    cumulativeMinMaxChecked, cumulativeMinMax4hChecked, cumulativeMinMax2hChecked,
    signalsLast3predChecked, signalsLast15minChecked, macdChecked, signalsMacdCrossChecked,
    vwapChecked,signalsVwapPriceChecked,
    signalsCons3Pred10MinChecked,signalsCons4Pred10MinChecked,signalsCons5Pred10MinChecked,
    signals2hMinMax160pctStockChecked,signals2hMinMax220pctStockChecked,
    signals2hMinMax160pctStockInsideChecked,signals2hMinMax220pctStockInsideChecked,
    signals2hMinMax220pctPredChecked,signals2hMinMax220pctStock3PredChecked,
    signalsSupRes1hStockChecked, supResLinesChecked, trendLinesChecked, lastPricesSupRes, lastPricesSupRes,
    showLegend,
    cancellationInterval,
    cumulativeMinMax2hOldChecked,
    signalsLast2hAvgStockChecked,
    signalsEma6PriceChecked, signalsEma12PriceChecked, signalsEma26PriceChecked, signalsEma120PriceChecked
  ]);

  return (
    <div className="admin-predictions">

      <div className="d-flex justify-content-center align-items-center gap-2">
        <div className="d-flex justify-content-start flex-row gap-3">
          <div className="d-flex flex-column">
            <Checkbox label="Pred Vals" value={predValsChecked} onChange={() => setPredValsChecked(!predValsChecked)} />
            <Checkbox label="Last5Preds Avg" value={last5AvgChecked} onChange={() => setLast5AvgChecked(!last5AvgChecked)} />
            <Checkbox label="Last3Preds Avg" value={last3AvgChecked} onChange={() => setLast3AvgChecked(!last3AvgChecked)} />
            <Checkbox label="Last15min Avg" value={last15minAvgChecked} onChange={() => setLast15minAvgChecked(!last15minAvgChecked)} />
            <Checkbox label="Last30min Avg" value={last30minAvgChecked} onChange={() => setLast30minAvgChecked(!last30minAvgChecked)} />
          </div>
          <div className="d-flex flex-column">
            <Checkbox label="Last1h Avg" value={last1hAvgChecked} onChange={() => setLast1hAvgChecked(!last1hAvgChecked)} />
            <Checkbox label="Last2h Avg" value={last2hAvgChecked} onChange={() => setLast2hAvgChecked(!last2hAvgChecked)} />
            <Checkbox label="Last3h Avg" value={last3hAvgChecked} onChange={() => setLast3hAvgChecked(!last3hAvgChecked)} />
            <Checkbox label="Last4h Avg" value={last4hAvgChecked} onChange={() => setLast4hAvgChecked(!last4hAvgChecked)} />
            <Checkbox label="Min/Max" value={cumulativeMinMaxChecked} onChange={() => setCumulativeMinMaxChecked(!cumulativeMinMaxChecked)} />
            <Checkbox label="Min/Max 4h" value={cumulativeMinMax4hChecked} onChange={() => setCumulativeMinMax4hChecked(!cumulativeMinMax4hChecked)} />
            <Checkbox label="Min/Max 2h" value={cumulativeMinMax2hChecked} onChange={() => setCumulativeMinMax2hChecked(!cumulativeMinMax2hChecked)} />
            <Checkbox label="Min/Max 2h old" value={cumulativeMinMax2hOldChecked} onChange={() => setCumulativeMinMax2hOldChecked(!cumulativeMinMax2hOldChecked)} />
            <Checkbox label="MACD" value={macdChecked} onChange={() => setMacdChecked(!macdChecked)} />
            <Checkbox label="VWAP" value={vwapChecked} onChange={() => setVwapChecked(!vwapChecked)} />
          </div>
          <div className="d-flex flex-column">
            <Checkbox label={SignalsMap.get(StockSignalsEnum.SIGNALS_4H)} value={signals4hChecked} onChange={() => setSignals4hChecked(!signals4hChecked)} />

            <Checkbox label={SignalsMap.get(StockSignalsEnum.SIGNALS_2H)} value={signals2hChecked} onChange={() => setSignals2hChecked(!signals2hChecked)} />
            <Checkbox label={SignalsMap.get(StockSignalsEnum.SIGNALS_2H_MIN_MAX_220PCT_STOCK_3PRED)} value={signals2hMinMax220pctStock3PredChecked} onChange={() => setSignals2hMinMax220pctStock3PredChecked(!signals2hMinMax220pctStock3PredChecked)} />

            <Checkbox label={SignalsMap.get(StockSignalsEnum.SIGNALS_2H_MIN_MAX_160PCT_STOCK)} value={signals2hMinMax160pctStockChecked} onChange={() => setSignals2hMinMax160pctStockChecked(!signals2hMinMax160pctStockChecked)} />
            <Checkbox label={SignalsMap.get(StockSignalsEnum.SIGNALS_2H_MIN_MAX_220PCT_STOCK)} value={signals2hMinMax220pctStockChecked} onChange={() => setSignals2hMinMax220pctStockChecked(!signals2hMinMax220pctStockChecked)} />

            <Checkbox label={SignalsMap.get(StockSignalsEnum.SIGNALS_2H_MIN_MAX_160PCT_STOCK_INSIDE)} value={signals2hMinMax160pctStockInsideChecked} onChange={() => setSignals2hMinMax160pctStockInsideChecked(!signals2hMinMax160pctStockInsideChecked)} />
            <Checkbox label={SignalsMap.get(StockSignalsEnum.SIGNALS_2H_MIN_MAX_220PCT_STOCK_INSIDE)} value={signals2hMinMax220pctStockInsideChecked} onChange={() => setSignals2hMinMax220pctStockInsideChecked(!signals2hMinMax220pctStockInsideChecked)} />

            <Checkbox label={SignalsMap.get(StockSignalsEnum.SIGNALS_2H_MIN_MAX_220PCT_PRED)} value={signals2hMinMax220pctPredChecked} onChange={() => setSignals2hMinMax220pctPredChecked(!signals2hMinMax220pctPredChecked)} />

            <Checkbox label={SignalsMap.get(StockSignalsEnum.SIGNALS_SUP_RES_1H_STOCK)} value={signalsSupRes1hStockChecked} onChange={() => setSignalsSupRes1hStockChecked(!signalsSupRes1hStockChecked)} />

            <Checkbox label={SignalsMap.get(StockSignalsEnum.SIGNALS_LAST_2H_AVG_STOCK)} value={signalsLast2hAvgStockChecked} onChange={() => setSignalsLast2hAvgStockChecked(!signalsLast2hAvgStockChecked)} />


          </div>
          <div className="d-flex flex-column">
             <Checkbox label={SignalsMap.get(StockSignalsEnum.SIGNALS_LAST_3PRED)} value={signalsLast3predChecked} onChange={() => setSignalsLast3predChecked(!signalsLast3predChecked)} />
            <Checkbox label={SignalsMap.get(StockSignalsEnum.SIGNALS_LAST_15MIN)} value={signalsLast15minChecked} onChange={() => setSignalsLast15minChecked(!signalsLast15minChecked)} />
            <Checkbox label={SignalsMap.get(StockSignalsEnum.SIGNALS_MACD_CROSS)} value={signalsMacdCrossChecked} onChange={() => setSignalsMacdCrossChecked(!signalsMacdCrossChecked)} />
            <Checkbox label={SignalsMap.get(StockSignalsEnum.SIGNALS_EMA6_PRICE)} value={signalsEma6PriceChecked} onChange={() => setSignalsEma6PriceChecked(!signalsEma6PriceChecked)} />
            <Checkbox label={SignalsMap.get(StockSignalsEnum.SIGNALS_EMA12_PRICE)} value={signalsEma12PriceChecked} onChange={() => setSignalsEma12PriceChecked(!signalsEma12PriceChecked)} />
            <Checkbox label={SignalsMap.get(StockSignalsEnum.SIGNALS_EMA26_PRICE)} value={signalsEma26PriceChecked} onChange={() => setSignalsEma26PriceChecked(!signalsEma26PriceChecked)} />
            <Checkbox label={SignalsMap.get(StockSignalsEnum.SIGNALS_EMA120_PRICE)} value={signalsEma120PriceChecked} onChange={() => setSignalsEma120PriceChecked(!signalsEma120PriceChecked)} />
            <Checkbox label={SignalsMap.get(StockSignalsEnum.SIGNALS_VWAP_PRICE)} value={signalsVwapPriceChecked} onChange={() => setGignalsVwapPriceChecked(!signalsVwapPriceChecked)} />
            <Checkbox label={SignalsMap.get(StockSignalsEnum.SIGNALS_CONSENSUS_3PRED_10M)} value={signalsCons3Pred10MinChecked} onChange={() => setSignalsCons3Pred10MinChecked(!signalsCons3Pred10MinChecked)} />
            <Checkbox label={SignalsMap.get(StockSignalsEnum.SIGNALS_CONSENSUS_4PRED_10M)} value={signalsCons4Pred10MinChecked} onChange={() => setSignalsCons4Pred10MinChecked(!signalsCons4Pred10MinChecked)} />
            <Checkbox label={SignalsMap.get(StockSignalsEnum.SIGNALS_CONSENSUS_5PRED_10M)} value={signalsCons5Pred10MinChecked} onChange={() => setSignalsCons5Pred10MinChecked(!signalsCons5Pred10MinChecked)} />

            Signal Cancellation Interval: <select onChange={(v)=>{setCancellationInterval(toIntOrDefault(v.target.value))}}>
              <option value="0">Off</option>
              <option value="180">3m</option>
              <option value="300">5m</option>
              <option value="600">10m</option>
              <option value="900">15m</option>
            </select>

            <Checkbox label="Show Legend" value={showLegend} onChange={() => setShowLegend(!showLegend)} />

            <Checkbox label="Show Sup/Res Lines" value={supResLinesChecked} onChange={() => setSupResLinesChecked(!supResLinesChecked)} />
            <Checkbox label="Show Trend Lines" value={trendLinesChecked} onChange={() => setTrendLinesChecked(!trendLinesChecked)} />
            <Checkbox label="Show Extra Sup/Res" value={lastPricesSupRes} onChange={() => setLastPricesSupRes(!lastPricesSupRes)} />
          </div>
        </div>
        <div className="flex-grow-1">
        <SearchFilter initialValues={initialValues} onClick={onClickCallback} />
        </div>
      </div>

      {(apiState === APIState.LOADING) && <Spinner />}

      {apiState === APIState.LOADED && macdChart && macdChecked &&
       <>
         <div>&nbsp;</div>
         <div style={{display:'block',clear:'both',}}><PredictionChart chartData={macdChart} /></div>
       </>
      }

      {apiState === APIState.LOADED && chartData &&
        <>
          <div><div><PredictionChart chartData={chartData} /></div></div>
        </>
      }
    </div>
  )
}
